import type { Setter } from "solid-js";
import type { SetStoreFunction } from "solid-js/store";
import { unwrap } from "solid-js/store";
import EventAnalytics from "~/js/utilities/EventAnalytics";
import type {
	ChatGptModeStateStore,
	IChatGptModes
} from "~/solidJs/entities/ChatGptEntities/ChatGptMainLayout";
import { ChatGptApi } from "~/solidJs/shared/api/ChatGptApi";
import { getPublicMessageFromError } from "~/solidJs/shared/helpers/errorHelpers";
import { getFileData, type IFileType } from "~/solidJs/shared/helpers/fileHelpers";
import type { TextMessage, TextOrFileMessage } from "~/solidJs/shared/helpers/types";
import { ChatGptStore } from "~/solidJs/widgets/ChatGpt/models/globalStore";

export interface IChatGptContextSetters {
	setChatGptModesStateStore: SetStoreFunction<ChatGptModeStateStore>;
	setChatGptInputState: Setter<string>;
	setFilesStore?: Setter<IFileType[]>;
}

export interface SendChatMessageArgs {
	mode: IChatGptModes;
	inputValue: string;
	files: IFileType[];
	contextSetters: IChatGptContextSetters;
	fetchImpl?: typeof ChatGptApi.getChatGpt4oDialogue;
	getChatGptModesStateStore: ChatGptModeStateStore;
}

export interface GeneratePromptArgs {
	inputValue: string;
	contextSetters: IChatGptContextSetters;
}

class ChatGptServiceClass {
	private modeCancelControllers = new Map<IChatGptModes, AbortController>();

	private getOrCreateAbortController(mode: IChatGptModes): AbortController {
		const existingController = this.modeCancelControllers.get(mode);
		if (existingController && !existingController.signal.aborted) {
			existingController.abort();
		}
		const newController = new AbortController();
		this.modeCancelControllers.set(mode, newController);
		return newController;
	}

	public createAssistantOptions(props?: Partial<SendChatMessageArgs>): SendChatMessageArgs {
		return {
			mode: "assistant",
			inputValue: "",
			files: [],
			contextSetters: {
				setChatGptInputState: () => null,
				setChatGptModesStateStore: ChatGptStore.setChatGptModesStateStore
			},
			fetchImpl: ChatGptApi.getChatGptAssistaintDialogue.bind(ChatGptApi),
			getChatGptModesStateStore: ChatGptStore.getChatGptModesStateStore,
			...(props || {})
		};
	}

	public async sendChatMessage(args: SendChatMessageArgs, eventSource = "user-send-message") {
		const { mode, inputValue, files, contextSetters, fetchImpl, getChatGptModesStateStore } =
			args;
		const { setChatGptModesStateStore, setChatGptInputState, setFilesStore } = contextSetters;

		const controller = this.getOrCreateAbortController(mode);

		try {
			setChatGptModesStateStore(mode, "error", null);
			setChatGptModesStateStore(mode, "isPending", true);
			const newMessage: TextMessage = {
				messageRole: "user",
				prompt: inputValue || (files.length > 0 ? " " : "")
			};

			const messagesToAdd: TextOrFileMessage[] = [];
			if (files.length > 0 && setFilesStore) {
				const fetchedFileData = await getFileData(files[0], controller.signal);
				messagesToAdd.push({ messageRole: "user", ...fetchedFileData });
			}
			messagesToAdd.push(newMessage);

			setChatGptModesStateStore(mode, "messages", (prevMessages) => [
				...prevMessages,
				...messagesToAdd
			]);
			setChatGptInputState("");
			setFilesStore?.([]);

			EventAnalytics.gptEvents.generateChatGptAnswer(mode, eventSource);
			const currentMessages = unwrap(getChatGptModesStateStore[mode].messages);
			const fetchMessage = fetchImpl || ChatGptApi.getChatGpt4oDialogue.bind(ChatGptApi);
			const responseMessage = await fetchMessage(currentMessages, controller.signal);
			setChatGptModesStateStore(mode, (prevModeState) => ({
				isPending: false,
				messages: [
					...prevModeState.messages,
					{
						messageRole: "assistant",
						prompt: responseMessage.message
					}
				]
			}));
		} catch (e) {
			setFilesStore?.([]);
			if (e instanceof Error && (e.name === "AbortError" || e.name === "CanceledError")) {
				return;
			}
			console.error(`Error sending message in mode ${mode}:`, e);
			setChatGptModesStateStore(mode, "error", getPublicMessageFromError(e));
			setChatGptModesStateStore(mode, "isPending", false);
		}
	}

	public async generatePrompt(args: GeneratePromptArgs, eventSource = "user-send-message") {
		const { inputValue, contextSetters } = args;
		const { setChatGptModesStateStore, setChatGptInputState } = contextSetters;
		const mode = "promptGeneration" as const;

		const controller = this.getOrCreateAbortController(mode);

		try {
			setChatGptModesStateStore(mode, "error", null);
			const userMessage: TextMessage = {
				prompt: inputValue,
				messageRole: "user"
			};
			setChatGptModesStateStore(mode, (_prevModeState) => ({
				isHintVisible: false,
				isPending: true,
				messages: [userMessage]
			}));
			setChatGptInputState("");

			EventAnalytics.gptEvents.generateChatGptAnswer("prompt-helper", eventSource);

			const responseMessage = await ChatGptApi.getChatGpt4oPromptWithoutTask(
				inputValue,
				controller.signal
			);

			setChatGptModesStateStore(mode, (prevModeState) => ({
				...prevModeState,
				isPending: false,
				messages: [
					...prevModeState.messages,
					{
						prompt: responseMessage.message,
						messageRole: "assistant"
					}
				]
			}));
		} catch (e) {
			if (controller.signal.aborted) {
				console.log("Prompt generation request aborted.");
				setChatGptModesStateStore(mode, "isPending", false);
				return;
			}
			console.error("Error generating prompt:", e);
			setChatGptModesStateStore(mode, "error", e instanceof Error ? e.message : String(e));
			setChatGptModesStateStore(mode, "isPending", false);
		}
	}

	public cancelGeneration(
		mode: IChatGptModes,
		setChatGptModesStateStore: SetStoreFunction<ChatGptModeStateStore>
	) {
		const controller = this.modeCancelControllers.get(mode);
		if (controller && !controller.signal.aborted) {
			controller.abort();
			console.log(`Cancellation explicitly requested for mode ${mode}.`);
		}
		setChatGptModesStateStore(mode, "isPending", false);
	}
}

export const ChatGptService = new ChatGptServiceClass();
